import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, Bike, Check, Loader2, X } from 'lucide-react';
import { useToast } from '../shared/Toast';
import { API_BASE_URL } from '../config/api';

/**
 * «Αλλαγή διανομέα» σε παραγγελία που είναι σε διανομή (απόφαση ιδιοκτήτη 12/10/2026: πάντα διαθέσιμη,
 * χωρίς διακόπτη). Δείχνει ποιος την έχει και τη δίνει σε άλλον ενεργό διανομέα:
 * POST /api/delivery/:storeId/orders/:orderId/reassign { driver_id } — αλλάζει ΜΟΝΟ ο διανομέας· το Ζ
 * διαβάζει το assigned_driver_id, άρα η είσπραξη περνά στον νέο (utils/registerStaffReport).
 * Ολοκληρωμένη παραγγελία δεν αλλάζει διανομέα (ο server απαντά 400/409).
 */

interface DriverRef { id: number; first_name: string; last_name: string }
interface OrderDriverInfo { order_id: number; status: string; driver: DriverRef | null; can_change: boolean }

const nameOf = (d: Pick<DriverRef, 'first_name' | 'last_name'>) => `${d.first_name || ''} ${d.last_name || ''}`.trim();

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('xentrix_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

interface DialogProps {
  storeId: number;
  orderId: number;
  shortCode?: string | null;
  current: DriverRef;
  onDone: (driver: DriverRef) => void;
  onBlocked: () => void;
  onClose: () => void;
}

const ChangeDriverDialog: React.FC<DialogProps> = ({ storeId, orderId, shortCode, current, onDone, onBlocked, onClose }) => {
  const { t } = useTranslation(['delivery']);
  const titleId = useId();
  const [drivers, setDrivers] = useState<DriverRef[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Η ίδια λίστα με την αποστολή (DispatchModal): ενεργοί διανομείς του καταστήματος.
        const res = await fetch(`${API_BASE_URL}/api/orders/${storeId}/drivers`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const list = (await res.json()) as DriverRef[];
        if (alive) setDrivers((Array.isArray(list) ? list : []).filter(d => d.id !== current.id));
      } catch {
        if (alive) setLoadFailed(true);
      }
    })();
    return () => { alive = false; };
  }, [storeId, current.id]);

  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) closeRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [saving]);

  const confirm = async () => {
    if (selected == null || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/delivery/${storeId}/orders/${orderId}/reassign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ driver_id: selected }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        const chosen = drivers?.find(d => d.id === selected);
        onDone(data.driver || chosen || { id: selected, first_name: '', last_name: '' });
        return;
      }
      if (data?.code === 'NOT_REASSIGNABLE' || data?.code === 'ORDER_CHANGED') {
        onBlocked();
        setError(t('delivery:changeDriver.notAllowed'));
      } else {
        setError(t('delivery:changeDriver.failed'));
      }
    } catch {
      setError(t('delivery:changeDriver.failed'));
    }
    setSaving(false);
  };

  const subtitle = shortCode
    ? t('delivery:changeDriver.subtitle', { code: shortCode, name: nameOf(current) })
    : t('delivery:changeDriver.subtitleNoCode', { name: nameOf(current) });

  return (
    <div className="cashier-overlay fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId}
        className="cashier-dialog w-full max-w-md bg-surface-0 rounded-2xl border border-line shadow-2xl overflow-hidden">
        <div className="cashier-dialog-header flex items-start justify-between gap-3 px-5 py-4 border-b border-line">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-bold text-t1">{t('delivery:changeDriver.title')}</h2>
            <p className="text-sm text-t3 truncate">{subtitle}</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} aria-label={t('delivery:changeDriver.close')}
            className="w-10 h-10 shrink-0 rounded-xl text-t3 hover:text-t1 hover:bg-surface-2 flex items-center justify-center">
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 max-h-[55vh] overflow-y-auto">
          {drivers === null && !loadFailed && (
            <p className="flex items-center gap-2 text-sm text-t3" role="status">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />{t('delivery:changeDriver.loading')}
            </p>
          )}
          {loadFailed && <p className="text-sm text-red-600 dark:text-red-400" role="alert">{t('delivery:changeDriver.loadFailed')}</p>}
          {drivers && drivers.length === 0 && <p className="text-sm text-t3">{t('delivery:changeDriver.none')}</p>}
          {drivers && drivers.length > 0 && (
            <div role="radiogroup" aria-label={t('delivery:changeDriver.pick')} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {drivers.map(d => {
                const checked = selected === d.id;
                return (
                  <button key={d.id} type="button" role="radio" aria-checked={checked} disabled={saving}
                    onClick={() => { setSelected(d.id); setError(null); }}
                    className={`flex items-center gap-3 min-h-[56px] px-3 py-2 rounded-xl border text-left transition-colors ${
                      checked ? 'border-brand bg-brand-soft' : 'border-line bg-surface-1 hover:bg-surface-2'}`}>
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      checked ? 'bg-brand text-brand-fg' : 'bg-surface-3 text-t2'}`} aria-hidden="true">
                      {checked ? <Check className="w-4 h-4" /> : `${(d.first_name || '?').charAt(0)}${(d.last_name || '').charAt(0)}`}
                    </span>
                    <span className={`text-sm font-semibold truncate ${checked ? 'text-brand' : 'text-t1'}`}>{nameOf(d) || '—'}</span>
                  </button>
                );
              })}
            </div>
          )}
          <p className="text-xs text-t4">{t('delivery:changeDriver.zNote')}</p>
          {error && <p role="alert" className="text-sm font-medium text-red-600 dark:text-red-400">{error}</p>}
        </div>

        <div className="flex gap-2 justify-end px-5 py-4 border-t border-line">
          <button type="button" onClick={onClose} disabled={saving}
            className="min-h-[44px] px-4 rounded-xl text-sm font-medium border border-line-strong bg-surface-2 text-t1 hover:bg-surface-3 disabled:opacity-50">
            {t('delivery:changeDriver.cancel')}
          </button>
          <button type="button" onClick={confirm} disabled={selected == null || saving} aria-busy={saving}
            className="cashier-primary inline-flex items-center justify-center gap-2 min-h-[44px] px-5 rounded-xl text-sm font-semibold bg-brand text-brand-fg hover:brightness-110 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <ArrowLeftRight className="w-4 h-4" aria-hidden="true" />}
            {t('delivery:changeDriver.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

interface Props {
  storeId: number;
  orderId: number;
  shortCode?: string | null;
}

/** Γραμμή «Διανομέας: …» + «Αλλαγή διανομέα». Χωρίς διανομέα (κούριερ πλατφόρμας, σφάλμα) → τίποτα. */
const OrderDriver: React.FC<Props> = ({ storeId, orderId, shortCode }) => {
  const { t } = useTranslation(['delivery']);
  const { showToast } = useToast();
  const [info, setInfo] = useState<OrderDriverInfo | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/delivery/${storeId}/orders/${orderId}/driver`, { headers: authHeaders() });
      setInfo(res.ok ? ((await res.json()) as OrderDriverInfo) : null);
    } catch {
      setInfo(null);
    }
  }, [storeId, orderId]);

  useEffect(() => { load(); }, [load]);

  if (!info?.driver) return null;
  const current = info.driver;

  return (
    <div className="px-5 py-3 border-b border-line/80 flex items-center gap-2.5" data-testid="order-driver">
      <Bike className="w-4 h-4 text-t3 shrink-0" aria-hidden="true" />
      <p className="flex-1 min-w-0 text-sm text-t2 truncate">
        <span className="text-t3">{t('delivery:changeDriver.label')}:</span>{' '}
        <span className="font-semibold text-t1">{nameOf(current) || '—'}</span>
      </p>
      {info.can_change && (
        <button type="button" onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-xl text-sm font-semibold border border-line-strong bg-surface-2 text-t1 hover:bg-surface-3 active:scale-[0.98] transition shrink-0">
          <ArrowLeftRight className="w-4 h-4" aria-hidden="true" />{t('delivery:changeDriver.action')}
        </button>
      )}
      {open && (
        <ChangeDriverDialog
          storeId={storeId}
          orderId={orderId}
          shortCode={shortCode}
          current={current}
          onClose={() => setOpen(false)}
          onBlocked={() => setInfo(prev => (prev ? { ...prev, can_change: false } : prev))}
          onDone={driver => {
            setOpen(false);
            setInfo(prev => (prev ? { ...prev, driver } : prev));
            showToast('success', t('delivery:changeDriver.changed', { name: nameOf(driver) }));
          }}
        />
      )}
    </div>
  );
};

export default OrderDriver;
